import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur } from "@/lib/api";
import { Btn, Field, inputCls } from "@/components/common";
import { calcTotals, descFor } from "@/lib/invoice";
import { Plus, Trash2 } from "lucide-react";

const EMPTY_CLIENT = { name: "", address: "", vat: "", cf: "", sdi: "", phone: "" };
const EMPTY_LINE = { description: "", qty: 1, unit: 0, discount: 0, vat: 0 };

export default function InvoiceForm({ initial, onSaved, onCancel }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState(() => ({
    number: "", date: new Date().toISOString().slice(0, 10), kind: "acconto", guest_name: "",
    payment_method: "Bonifico Bancario", notes: "", lines: [{ ...EMPTY_LINE }], ...initial,
    client: { ...EMPTY_CLIENT, ...(initial?.client || {}) },
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setClient = (k, v) => setF((p) => ({ ...p, client: { ...p.client, [k]: v } }));
  const setLine = (i, k, v) => setF((p) => ({ ...p, lines: p.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));

  useEffect(() => {
    if (isEdit || !f.date) return;
    api.get("/invoices/next-number", { params: { date: f.date } }).then((r) => set("number", r.data.number)).catch(() => {});
  }, [f.date, isEdit]);

  const changeKind = (kind) => {
    setF((p) => {
      const lines = [...p.lines];
      if (lines[0]) {
        lines[0] = { ...lines[0], description: descFor(kind, p.guest_name) };
        if (p._amounts) lines[0].unit = p._amounts[kind];
      }
      return { ...p, kind, lines };
    });
  };

  const totals = calcTotals(f.lines);

  const save = async () => {
    if (!f.client.name.trim()) { toast.error("Inserisci la ragione sociale del cliente"); return; }
    if (!f.lines.length) { toast.error("Aggiungi almeno una riga"); return; }
    setSaving(true);
    try {
      const { _amounts, ...rest } = f;
      const body = { ...rest, lines: f.lines.map((l) => ({ ...l, qty: Number(l.qty) || 0, unit: Number(l.unit) || 0, discount: Number(l.discount) || 0, vat: Number(l.vat) || 0 })) };
      const { data } = isEdit ? await api.put(`/invoices/${f.id}`, body) : await api.post("/invoices", body);
      toast.success(isEdit ? "Fattura aggiornata" : `Fattura ${data.number} creata`);
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="invoice-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Numero"><input data-testid="inv-number" className={inputCls} value={f.number} onChange={(e) => set("number", e.target.value)} /></Field>
        <Field label="Data documento"><input data-testid="inv-date" type="date" className={inputCls} value={f.date} onChange={(e) => set("date", e.target.value)} /></Field>
        <Field label="Tipo">
          <select data-testid="inv-kind" className={inputCls} value={f.kind} onChange={(e) => changeKind(e.target.value)}>
            <option value="acconto">Acconto</option><option value="saldo">Saldo</option><option value="altro">Altro</option>
          </select>
        </Field>
      </div>
      <Field label="Ospite (riferimento)"><input data-testid="inv-guest" className={inputCls} value={f.guest_name} onChange={(e) => set("guest_name", e.target.value)} placeholder="fam. Rossi" /></Field>
      <ClientFields c={f.client} setClient={setClient} />
      <LinesEditor lines={f.lines} setLine={setLine} setLines={(l) => set("lines", l)} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Metodo di pagamento"><input data-testid="inv-payment" className={inputCls} value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} /></Field>
        <Field label="Note (opzionale)"><input data-testid="inv-notes" className={inputCls} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
      </div>
      <div className="flex items-center justify-between border-t border-slate-200 pt-4">
        <div className="text-sm text-slate-500">Totale <b className="text-lg text-slate-900 tabular" data-testid="inv-total">{eur(totals.total)}</b></div>
        <div className="flex gap-2">
          <Btn variant="secondary" data-testid="inv-cancel" onClick={onCancel}>Annulla</Btn>
          <Btn data-testid="inv-save" onClick={save} disabled={saving}>{saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea fattura"}</Btn>
        </div>
      </div>
    </div>
  );
}

function ClientFields({ c, setClient }) {
  const fields = [["name", "Ragione sociale *"], ["address", "Indirizzo"], ["vat", "P.IVA / TAX ID"], ["cf", "Codice fiscale"], ["sdi", "Codice SDI"], ["phone", "WhatsApp (per invio)"]];
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Informazioni cliente (agenzia)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map(([k, l]) => (
          <Field key={k} label={l}><input data-testid={`inv-client-${k}`} className={inputCls} value={c[k]} onChange={(e) => setClient(k, e.target.value)} /></Field>
        ))}
      </div>
    </div>
  );
}

function LinesEditor({ lines, setLine, setLines }) {
  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Righe</div>
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-6 gap-2 items-end border-b border-slate-100 pb-3 last:border-0" data-testid={`inv-line-${i}`}>
          <div className="col-span-6"><Field label="Descrizione"><input data-testid={`inv-line-desc-${i}`} className={inputCls} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} /></Field></div>
          <div className="col-span-1"><Field label="Q.tà"><input data-testid={`inv-line-qty-${i}`} type="number" className={inputCls} value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} /></Field></div>
          <div className="col-span-2"><Field label="Unit. €"><input data-testid={`inv-line-unit-${i}`} type="number" className={inputCls} value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} /></Field></div>
          <div className="col-span-1"><Field label="Sconto €"><input data-testid={`inv-line-discount-${i}`} type="number" className={inputCls} value={l.discount} onChange={(e) => setLine(i, "discount", e.target.value)} /></Field></div>
          <div className="col-span-1"><Field label="IVA %"><input data-testid={`inv-line-vat-${i}`} type="number" className={inputCls} value={l.vat} onChange={(e) => setLine(i, "vat", e.target.value)} /></Field></div>
          <button data-testid={`inv-line-del-${i}`} onClick={() => setLines(lines.filter((_, j) => j !== i))} className="col-span-1 p-2 text-slate-400 hover:text-red-600 justify-self-end"><Trash2 size={16} /></button>
        </div>
      ))}
      <button data-testid="inv-line-add" onClick={() => setLines([...lines, { ...EMPTY_LINE }])} className="text-sm font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"><Plus size={15} /> Aggiungi riga</button>
    </div>
  );
}
